import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Form,
    Button,
    Container,
    Row,
    Col,
    ListGroup,
    Modal,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";
import { Formik, Field, Form as FormikForm } from "formik";

import AddGemstoneForm from "../addItems/AddGemstoneForm";
import AddRudrakshForm from "../addItems/AddRudrakshForm";
import "./ItemsDefault.css";

const UploadPage = () => {
    const { type } = useParams();
    const navigate = useNavigate();
    const apiUrl = "https://jwapi.infosware-test.in";

    const [excelData, setExcelData] = useState([]);
    const [file, setFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [fileObjects, setFileObjects] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [showGemstoneModal, setShowGemstoneModal] = useState(false);
    const [showRudrakshModal, setShowRudrakshModal] = useState(false);
    const [rfidInputState, setRfidInputState] = useState({});
    const [gemstones, setGemstones] = useState([]);
    const [rudrakshs, setRudrakshs] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [otherItems, setOtherItems] = useState([]);
    const [tag, setTag] = useState("");
    const [submittedItems, setSubmittedItems] = useState(() => {
        const saved = localStorage.getItem("submittedItems");
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        // Load saved excel data and submitted items from localStorage
        const savedData = localStorage.getItem("excelData");
        const savedSubmittedItems = localStorage.getItem("submittedItems");

        if (savedData) {
            const parsedData = JSON.parse(savedData);
            setExcelData(parsedData);
            setShowPreview(true);
        }

        if (savedSubmittedItems) {
            const parsedSubmittedItems = JSON.parse(savedSubmittedItems);
            setSubmittedItems(parsedSubmittedItems);
        }
    }, []);

    useEffect(() => {
        // Persist submitted items to localStorage whenever it changes
        localStorage.setItem("submittedItems", JSON.stringify(submittedItems));
    }, [submittedItems]);

    useEffect(() => {
        // Validate submitted items against loaded Excel data
        if (excelData.length > 0) {
            const validIds = excelData.map((item) => item.id);
            setSubmittedItems((prev) => prev.filter((id) => validIds.includes(id)));
        }
    }, [excelData]);

    useEffect(() => {
        if (selectedItem) {
            setRfidInputState((prev) => ({
                ...prev,
                [selectedItem.id]: { isFetched: false },
            }));
        }
    }, [selectedItem]);

    // Handle file selection
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setShowPreview(false);
        }
    };

    // Upload Excel
    const handleUpload = async () => {
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await axios.post(`${apiUrl}/api/${type}/upload-excel`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const uploadedData = response.data.data.map((item) => ({ ...item, type }));
            setExcelData(uploadedData);
            setShowPreview(true);
            localStorage.setItem("excelData", JSON.stringify(uploadedData));
            setFile(null);
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} Excel uploaded successfully!`);
        } catch (error) {
            toast.error("Failed to upload Excel file. Please try again.");
            console.error(error);
        }
    };

    // Open modal
    const openAddModal = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    // Fetch RFID Tag
    const fetchTagID = async (setFieldValue) => {
        try {
            const res = await fetch(`${apiUrl}/api/read-tag`);
            const data = await res.json();
            setTag(data.tag);
            setFieldValue("rfidNo", data.tag);
        } catch (err) {
            console.error("Error fetching tag ID:", err);
        }
    };

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);
    
        const validFiles = [];
        let hasInvalid = false;
    
        files.forEach((file) => {
            const isValidType = ["image/jpeg", "image/png"].includes(file.type);
            const isValidSize = file.size <= 200 * 1024;
    
            if (isValidType && isValidSize) {
                validFiles.push(file);
            } else {
                hasInvalid = true;
            }
        });
    
        if (hasInvalid) {
            toast.warning("Only JPG/PNG images under 200KB are allowed.");
        }
    
        const limited = validFiles.slice(0, 4 - fileObjects.length);
        setFileObjects((prev) => [...prev, ...limited]);
        setPhotos((prev) => [...prev, ...limited.map((f) => f.name)]);
    };
    

   const handleAddDetails = async () => {
    if (!selectedItem || !tag || fileObjects.length === 0) {
        toast.error("All fields (RFID and at least one photo) are required!");
        return;
    }

    const formData = new FormData();
    formData.append("id", selectedItem.id);
    formData.append("rfidNo", tag);
    formData.append("otheritems", JSON.stringify(otherItems));
    Object.keys(selectedItem).forEach((key) => {
        if (key !== "id") formData.append(key, selectedItem[key] || "");
    });
    fileObjects.forEach((file) => formData.append("photos", file));

    try {
        const res = await axios.put(`${apiUrl}/api/${type}/${selectedItem.id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.status === 200) {
            toast.success("Details added successfully!");
            setSubmittedItems((prev) => [...prev, selectedItem.id]);
            setShowModal(false);
            setOtherItems([]);
            setFileObjects([]);
            setPhotos([]);
            setTag("");
            setSelectedItem(null);

            // Clear the list if all items submitted
            const allIds = excelData.map((item) => item.id);
            const allSubmitted = allIds.every((id) => submittedItems.includes(id) || id === selectedItem.id);
            if (allSubmitted) {
                setExcelData([]);
                setShowPreview(false);
                localStorage.removeItem("excelData");
            }
        }
    } catch (err) {
        if (err.response && err.response.data && err.response.data.message === "RFID already assigned") {
            // Show specific error for duplicate RFID
            toast.error("RFID already assigned to another item.");
        } else {
            // Generic error message
            toast.error("Failed to add details.");
        }
        console.error(err);
    }
};
    
    const handleDeleteGemstone = (index) => {
        const updatedGemstones = [...gemstones];
        updatedGemstones.splice(index, 1);
        setGemstones(updatedGemstones);
    };
    const handleDeleteRudraksh = (index) => {
        const updatedRudrakshs = [...rudrakshs];
        updatedRudrakshs.splice(index, 1);
        setRudrakshs(updatedRudrakshs);
    };

    const handleDeletePhoto = (index) => {
        const updatedFileObjects = [...fileObjects];
        updatedFileObjects.splice(index, 1);
        setFileObjects(updatedFileObjects);

        const updatedPhotos = [...photos];
        updatedPhotos.splice(index, 1);
        setPhotos(updatedPhotos);
    };

    return (
        <Container className="upload-container mt-5">
            <Row className="mb-3">
                <Col>
                    <h3>Upload {type.charAt(0).toUpperCase() + type.slice(1)} Excel File</h3>
                </Col>
            </Row>

            <Form className="d-flex align-items-center mb-4">
                <Form.Control type="file" accept=".xlsx, .xls" onChange={handleFileChange} className="me-2" />
                <Button variant="" className="upload-button" onClick={handleUpload} disabled={!file}>
                    Upload
                </Button>
            </Form>

            {showPreview && excelData.length > 0 && (
                <div className="excel-preview">
                    <h5>Preview</h5>
                    <ListGroup>
                        {excelData
                            .filter((item) => item.type === type) // Filter by type
                            .map((item, index) => (
                                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                    <span>{item.name || item.items || "Unnamed Item"}</span>
                                    {submittedItems.includes(item.id) ? (
                                        <span className="btn-success" style={{ fontSize: "1.2rem" }}>✅</span>
                                    ) : (
                                        <Button size="sm" variant="primary" onClick={() => openAddModal(item)}>Add</Button>
                                    )}
                                </ListGroup.Item>
                            ))}
                    </ListGroup>
                </div>
            )}

            {/* Main Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" backdrop="static" dialogClassName="custom-modal">
                <Modal.Header id="modalheader" closeButton>
                    <Modal.Title>Add Details for -  {selectedItem?.items}</Modal.Title>
                </Modal.Header>
                <Modal.Body id="modalbody">
                    {/* RFID */}
                                        {/* RFID */}
                    <Formik
                        initialValues={{ rfidNo: "" }} // Initial value for rfidNo
                        onSubmit={(values) => {
                            console.log(values);
                        }}
                    >
                        {({ setFieldValue, values }) => (
                            <FormikForm>
                                <div className="rfid-container">
                                    <Field
                                        name="rfidNo"
                                        placeholder="Enter RFID"
                                        className="form-control rfid-input"
                                        value={values.rfidNo}
                                        readOnly
                                    />
                                    {!rfidInputState[selectedItem?.id]?.isFetched && (
                                        <Button
                                            variant="primary"
                                            onClick={() => fetchTagID(setFieldValue)}
                                            className="rfid-button"
                                        >
                                            Add RFID Tag
                                        </Button>
                                    )}
                                </div>
                            </FormikForm>
                        )}
                    </Formik>

                    {/* Conditionally Render Type Selector */}
                    {(type === "jewellery" || type === "watch") && (
                        <Form.Group controlId="formGemstone">
                            <Form.Label>Select Gemstone/Rudraksh</Form.Label>
                            <Form.Select
                                onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    if (selectedValue === 'gemstone') {
                                        setShowGemstoneModal(true);
                                    } else if (selectedValue === 'rudraksh') {
                                        setShowRudrakshModal(true);
                                    }
                                }}
                            >
                                <option value="">Select Item</option>
                                <option value="gemstone">Gemstone</option>
                                <option value="rudraksh">Rudraksh</option>
                            </Form.Select>
                            {/* Display Added Gemstones */}
                            {gemstones.length > 0 && (
                                <div className="gemstone-grid mt-2">
                                    {gemstones.map((gemstone, index) => (
                                        <div key={index} className="gemstone-item">
                                            <span className="gemstone-name">{gemstone.gemstoneName || "Unnamed Gemstone"}</span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger p-0"
                                                onClick={() => handleDeleteGemstone(index)}
                                            >
                                                <FaTrash />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Display Added Rudrakshs */}
                            {rudrakshs.length > 0 && (
                                <div className="rudraksh-grid mt-2">
                                    {rudrakshs.map((rudraksh, index) => (
                                        <div key={index} className="rudraksh-item">
                                            <span className="rudraksh-name">{rudraksh.rudrakshName || "Unnamed Rudraksh"}</span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger p-0"
                                                onClick={() => handleDeleteRudraksh(index)}
                                            >
                                                <FaTrash />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Form.Group>
                    )}

                    {/* Photos */}
                    <Form.Group controlId="formPhotos" className="mt-3">
                        <Form.Label>Photos</Form.Label>
                        <Form.Control
                            type="file"
                            multiple
                            accept="image/jpeg, image/png"
                            onChange={handlePhotoChange}
                        />
                        <div className="mt-2 d-flex flex-wrap">
                            {fileObjects.map((file, index) => (
                                <div key={index} className="d-flex align-items-center me-3 mt-2">
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        style={{
                                            width: "90px",
                                            height: "40px",
                                            objectFit: "cover",
                                            marginRight: "5px",
                                        }}
                                    />
                                    <span className="me-2">{file.name}</span>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        onClick={() => handleDeletePhoto(index)}
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </Form.Group>
                    <Button className="mt-5 flex-end" variant="success" onClick={handleAddDetails}>
                        Submit
                    </Button>
                </Modal.Body>
                <Modal.Footer id="modalheader" >
                </Modal.Footer>
            </Modal>

            {/* Gemstone Modal */}
            <Modal show={showGemstoneModal} onHide={() => setShowGemstoneModal(false)} backdrop="static" dialogClassName="custom-modal">
                <Modal.Header id="modalheader" closeButton>
                    <Modal.Title>Add New Gemstone</Modal.Title>
                </Modal.Header>
                <Modal.Body id="modalbody">
                    <AddGemstoneForm
                        handleGemstoneSubmit={(gemstoneData) => {
                            const updated = [...gemstones, gemstoneData];
                            setGemstones(updated);
                            setOtherItems((prev) => [...prev, { ...gemstoneData, type: 'gemstone' }]); // Add to otherItems
                            setShowGemstoneModal(false);
                        }}
                    />
                </Modal.Body>
                <Modal.Footer id="modalheader" >
                </Modal.Footer>
            </Modal>

            {/* Rudraksh Modal */}
            <Modal show={showRudrakshModal} onHide={() => setShowRudrakshModal(false)} backdrop="static" dialogClassName="custom-modal">
                <Modal.Header id="modalheader" closeButton>
                    <Modal.Title>Add New Rudraksh</Modal.Title>
                </Modal.Header>
                <Modal.Body id="modalbody">
                    <AddRudrakshForm
                        handleRudrakshSubmit={(rudrakshData) => {
                            const updated = [...rudrakshs, rudrakshData];
                            setRudrakshs(updated);
                            setOtherItems((prev) => [...prev, { ...rudrakshData, type: 'rudraksh' }]); // Add to otherItems
                            setShowRudrakshModal(false);
                        }}
                    />
                </Modal.Body>
                <Modal.Footer id="modalheader" >
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default UploadPage;